import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const DraggableBeforeAfter = ({ beforeImage, afterImage, title }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = (x / rect.width) * 100;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setSliderPosition(percentage));
  }, []);

  const handleMouseDown = (e) => { e.preventDefault(); setIsDragging(true); handleMove(e.clientX); };
  const handleTouchStart = (e) => { setIsDragging(true); handleMove(e.touches[0].clientX); };

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging) return;
      const clientX = e.type === 'mousemove' ? e.clientX : e.touches?.[0]?.clientX;
      if (clientX !== undefined) handleMove(clientX);
    };
    const onUp = () => { setIsDragging(false); if (rafRef.current) cancelAnimationFrame(rafRef.current); };

    if (isDragging) {
      window.addEventListener('mousemove', onMove, { passive: true });
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove, { passive: true });
      window.addEventListener('touchend', onUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDragging, handleMove]);

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-xl select-none cursor-col-resize border border-themed bg-[#0A0B0E]"
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      data-testid="draggable-comparison-container"
    >
      {/* After (background) */}
      <div className="relative w-full h-72 md:h-80 lg:h-96">
        <img src={afterImage} alt="After" className="absolute inset-0 w-full h-full object-contain" draggable="false" referrerPolicy="no-referrer" loading="lazy" />
        <div className="absolute top-3 right-3 bg-[#D4AF37] text-[#0A0B0E] px-3 py-1 rounded-full text-[10px] font-sans font-bold uppercase tracking-wider z-10">After</div>
      </div>

      {/* Before (clipped) */}
      <div className="absolute inset-0 will-change-[clip-path]" style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}>
        <img src={beforeImage} alt="Before" className="absolute inset-0 w-full h-full object-contain" draggable="false" referrerPolicy="no-referrer" loading="lazy" />
        <div className="absolute top-3 left-3 bg-[#0A0B0E]/80 text-[#F8FAFC] px-3 py-1 rounded-full text-[10px] font-sans font-bold uppercase tracking-wider z-10 border border-white/10">Before</div>
      </div>

      {/* Slider */}
      <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}>
        <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[2px] bg-[#D4AF37]/80" />
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center pointer-events-auto cursor-col-resize transition-transform duration-200 slider-handle-glow ${isDragging ? 'scale-110' : 'hover:scale-110'}`}
          style={{
            background: 'linear-gradient(135deg, #D4AF37, #E5C158)',
            boxShadow: '0 2px 16px rgba(212,175,55,0.4), 0 0 0 2px rgba(255,255,255,0.6)',
          }}
          data-testid="hero-slider-handle"
        >
          <ChevronLeft size={12} className="text-[#0A0B0E] -mr-0.5" />
          <ChevronRight size={12} className="text-[#0A0B0E] -ml-0.5" />
        </div>
      </div>
    </div>
  );
};

export default DraggableBeforeAfter;
